<script setup lang="ts">

import { inject, ref} from "vue";
import routeManager from "@/router/route-manager";
import { labelServiceSymbol } from "@/plugins/LabelServicePlugin";
import type { LabelService } from "@/composables/label-service";
import { ConfigurationError } from "@/types/errors";
import { SimpleRoutingParams } from "@/types/routing";

const labelService = inject<LabelService>( labelServiceSymbol ) as LabelService;
const legal = routeManager.legalNavigation();
const privacyPolicy = ref< SimpleRoutingParams >( findRoutingConfig( legal, 'lblPrivacyPolicy' )! );
const refundPolicy = ref< SimpleRoutingParams >( findRoutingConfig( legal, 'lblRefundPolicy' )! );
const smsTextPolicy = ref< SimpleRoutingParams >( findRoutingConfig( legal, 'lblSmsPolicy' )! );
const termsAndConditions = ref< SimpleRoutingParams >( findRoutingConfig( legal, 'lblTermsAndConditions' )! );
const whitelistInstructions = ref< SimpleRoutingParams >( findRoutingConfig( legal, 'lblWhitelistInstructions' )! );

function hideMenuOnBlur( event: MouseEvent ) {
  if ( event.target ) {
    ( event.target as Element ).removeAttribute( "open" );
  }
}

function showMenuOnFocus( event: Event ) {
  if ( event.target ) {
    ( event.target as Element ).setAttribute( "open", "" );
  }
}

function findRoutingConfig( source: SimpleRoutingParams[], itemKey: string ): SimpleRoutingParams {
  const config = source.find( ( { key } ) => key === itemKey )!;
  if ( !config ) {
    throw new ConfigurationError(
        `The route configuration with key '${ itemKey }' is not currently defined. Update the definition in routeManager and rebuild.`
    )
  }

  return config;
}
</script>

<template>
  <details @mouseenter="showMenuOnFocus" @mouseleave="hideMenuOnBlur" class="header-menu dropdown dropdown-end dropdown-hover">
    <summary class="btn btn-sm btn-ghost text-base">
      <i class="text-primary pi pi-question-circle lg:inline"></i>
      <span class="hidden lg:inline items-center gap-2 text-base">{{ labelService.label( 'lblSupport' ) }}</span>
    </summary>
    <ul class="menu dropdown-content w-min-58" @click="hideMenuOnBlur">
      <li>
        <router-link :to="routeManager.contactUsPath()">
          <i class="text-primary pi pi-phone"></i><span class="ml-2 text-base">{{ labelService.label( 'lblContactUs' ) }}</span>
        </router-link>
      </li>
      <li>
        <router-link :to="routeManager.appToolsPath()">
          <i class="text-primary pi pi-eraser"></i><span class="ml-2 text-base">{{ labelService.label( 'lblReset' ) }}</span>
        </router-link>
      </li>
      <li>
        <router-link :to="privacyPolicy.path">
          <i class="text-primary pi pi-list-check"></i><span class="ml-2 text-base">{{ labelService.label( privacyPolicy.key ) }}</span>
        </router-link>
      </li>
      <li>
        <router-link :to="refundPolicy.path">
          <i class="text-primary pi pi-list-check"></i><span class="ml-2 text-base">{{ labelService.label( refundPolicy.key ) }}</span>
        </router-link>
      </li>
      <li>
        <router-link :to="smsTextPolicy.path">
          <i class="text-primary pi pi-list-check"></i><span class="ml-2 text-base">{{ labelService.label( smsTextPolicy.key ) }}</span>
        </router-link>
      </li>
      <li>
        <router-link :to="termsAndConditions.path">
          <i class="text-primary pi pi-list-check"></i><span class="ml-2 text-base">{{ labelService.label( termsAndConditions.key ) }}</span>
        </router-link>
      </li>
      <li>
        <router-link :to="whitelistInstructions.path">
          <i class="text-primary pi pi-list-check"></i><span class="ml-2 text-base">{{ labelService.label( whitelistInstructions.key ) }}</span>
        </router-link>
      </li>
    </ul>
  </details>
</template>
